<template>
  <section class="creator-visit-timeline">
    <div class="creator-visit-timeline__header">
      <h3 class="global-h3 creator-visit-timeline__title">How a creator visit comes together</h3>
      <p class="creator-visit-timeline__aside">Post early. Everything after that runs on a set clock.</p>
    </div>

    <ol class="creator-visit-timeline__steps">
      <li v-for="step in steps" :key="step.number" class="creator-visit-timeline__step">
        <div class="creator-visit-timeline__marker">
          <span class="creator-visit-timeline__badge" aria-hidden="true">{{ step.number }}</span>
        </div>
        <p class="creator-visit-timeline__eyebrow">{{ step.eyebrow }}</p>
        <p class="creator-visit-timeline__heading">{{ step.heading }}</p>
        <p v-if="step.body" class="creator-visit-timeline__body">{{ step.body }}</p>
        <div v-if="step.callouts" class="creator-visit-timeline__callouts">
          <p v-for="callout in step.callouts" :key="callout.lead" class="creator-visit-timeline__callout">
            <b>{{ callout.lead }}</b> {{ callout.text }}
          </p>
        </div>
      </li>
    </ol>
  </section>
</template>

<script setup lang="ts">
type TimelineCallout = {
  lead: string;
  text: string;
};

type TimelineStep = {
  number: number;
  eyebrow: string;
  heading: string;
  body?: string;
  callouts?: TimelineCallout[];
};

const steps: TimelineStep[] = [
  {
    number: 1,
    eyebrow: "Post the visit",
    heading: "Give creators a heads up",
    callouts: [
      { lead: "4–6 wks", text: "before your timeframe (for a 1-2 month visit window)" },
      { lead: "6–8 wks", text: "before a specific event" },
    ],
  },
  {
    number: 2,
    eyebrow: "Creators apply",
    heading: "~7 days",
    body: "Applications stay open for a week by default, then you pick your creator.",
  },
  {
    number: 3,
    eyebrow: "The visit",
    heading: "Your dates",
    body: "The creator explores on the timeframe you set.",
  },
  {
    number: 4,
    eyebrow: "Content delivered",
    heading: "within 10 days",
    body: "Photos, videos and adventures land in your library for your review.",
  },
];
</script>

<style scoped lang="scss">
@import "@/scss/variables.scss";

.creator-visit-timeline {
  --step-gap: 28px;

  width: calc(100% + 50px);
  margin-top: 53px;
  margin-left: -25px;
  padding: 22px 24px 26px;
  background: #fff;
  border: 1px solid #ccd3d7;
  border-radius: 6px;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 24px;
    margin-bottom: 22px;
  }

  &__title {
    flex: 0 1 auto;
  }

  &__aside {
    flex: 0 0 auto;
    margin: 0;
    color: $gray;
    font:
      14px/18px "Quicksand",
      sans-serif;
    text-align: right;
    white-space: nowrap;
  }

  &__steps {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: var(--step-gap);
    margin: 0;
    padding: 0;
    list-style: none;
  }

  &__step {
    position: relative;
    min-width: 0;
  }

  &__marker {
    position: relative;
    height: 30px;
    margin-bottom: 12px;
  }

  &__step:not(:last-child) &__marker::after {
    content: "";
    position: absolute;
    z-index: 0;
    top: 50%;
    left: 15px;
    width: calc(100% + var(--step-gap));
    height: 2px;
    background: $brand-color-lighter;
    transform: translateY(-50%);
  }

  &__badge {
    width: 30px;
    height: 30px;
    border-radius: 100px;
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    z-index: 1;
    background: $brand-color;
    color: #fff;
    font:
      700 14px/14px "Quicksand",
      sans-serif;
  }

  &__eyebrow {
    margin: 0 0 4px;
    color: $brand-color;
    font:
      700 12px/16px "Quicksand",
      sans-serif;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  &__heading {
    margin: 0 0 8px;
    color: #000;
    font:
      700 16px/20px "Quicksand",
      sans-serif;
  }

  &__body {
    margin: 0;
    color: #000;
    font:
      14px/18px "Quicksand",
      sans-serif;
  }

  &__callouts {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__callout {
    margin: 0;
    padding: 8px 10px;
    border-radius: 8px;
    background: mix(#fff, $yellow, 78%);
    color: #000;
    font:
      14px/18px "Quicksand",
      sans-serif;

    b {
      font-weight: 700;
    }
  }
}

@media (max-width: $tablet-large-max-width) {
  .creator-visit-timeline {
    --step-gap: 22px;

    &__header {
      flex-direction: column;
      align-items: flex-start;
      gap: 6px;
    }

    &__aside {
      text-align: left;
      white-space: normal;
    }

    &__steps {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    &__step:nth-child(2n) &__marker::after {
      display: none;
    }
  }
}

@media (max-width: $mobile-max-width) {
  .creator-visit-timeline {
    --step-gap: 18px;

    width: calc(100% + 30px);
    margin-left: -15px;
    padding: 18px 16px 20px;

    &__steps {
      grid-template-columns: minmax(0, 1fr);
    }

    &__marker::after {
      display: none;
    }
  }
}
</style>
